import ClickEvent = JQuery.ClickEvent;
import { fromEvent } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import ScrollSpy from 'bootstrap/js/dist/scrollspy';

const SKETCH_HASH_PREFIX = '#projects/sketch-';
const MERGE_COMPANY_BREAKPOINT = 991;
const SCROLL_HASH_MIN_WIDTH = 991;
const NAV_LINKS_SELECTOR = '.app-header a[data-scroll], .app-mobile-nav a[data-scroll]';
const HISTORY_DEDUPE_MS = 180;

export interface ScrollNavConfig {
  linkSelector: string;
  sectionSelector: string;
  headerSelector: string;
  headerPinned?: boolean;
  headerOffsetTop?: number;
  headerPinnedSelector?: string;
  syncHashOnScroll?: boolean;
}

export class ScrollNavModule {
  #linkSelector: string;
  #headerSelector: string;
  #headerPinnedSelector: string;
  #headerOffsetTop: number;
  #syncHashOnScroll: boolean;

  #scrollSpy: { refresh(): unknown; dispose(): unknown } | null = null;
  #isProgrammaticScroll = false;
  #historyBlockedUntil = 0;
  #lastPushedHash = '';
  #lastPushedAt = 0;

  constructor(config: ScrollNavConfig) {
    this.#linkSelector = config.linkSelector;
    this.#headerSelector = config.headerSelector;
    this.#headerPinnedSelector = config.headerPinnedSelector || 'app-header-pinned';
    this.#headerOffsetTop = typeof config.headerOffsetTop === 'number' ? config.headerOffsetTop : 400;
    this.#syncHashOnScroll = typeof config.syncHashOnScroll === 'boolean' ? config.syncHashOnScroll : true;

    this.#applyCompanyTabsMergeState();
    this.#setupScrollSpy();
    this.#setupEvents();
    this.#syncActiveStateFromLocation();
    this.#handleHeaderPinnedState();
  }

  #setupScrollSpy() {
    this.#scrollSpy?.dispose();

    const target = document.querySelector('.app-header') || document.body;
    this.#scrollSpy = ScrollSpy.getOrCreateInstance(document.body, {
      target,
      offset: this.#getHeaderHeight() + 32,
      smoothScroll: false,
      threshold: [0.1, 0.5, 1],
    });

    this.#scrollSpy.refresh();
  }

  #setupEvents() {
    $(document).on('click', this.#linkSelector, this.#onLinkClick.bind(this));
    $(window).on('hashchange', this.#onHashChange.bind(this));

    document.body.addEventListener('activate.bs.scrollspy', this.#onScrollSpyActivate as EventListener);

    fromEvent(window, 'scroll')
      .pipe(auditTime(80))
      .subscribe(() => this.#onScroll());

    fromEvent(window, 'resize')
      .pipe(auditTime(160))
      .subscribe(() => this.#onViewportChanged());

    $(window).on('orientationchange load', () => this.#onViewportChanged());
  }

  #onViewportChanged() {
    this.#applyCompanyTabsMergeState();
    this.#setupScrollSpy();
    this.#syncActiveStateFromLocation();
  }

  #onScrollSpyActivate = (event: Event) => {
    if (!this.#shouldUpdateHashOnScroll() || this.#isProgrammaticScroll) {
      return;
    }

    const hash = this.#normalizeHash(this.#extractHashFromActivateEvent(event));
    if (!hash || this.#isSketchHash(hash)) {
      return;
    }

    // On stacked mobile layout about/company is handled with explicit offsets in onScroll().
    if (!this.#shouldMergeCompanyTabs() && (hash === '#about' || hash === '#company')) {
      return;
    }

    this.#setActiveLinks(hash);
    this.#pushHash(hash);
  };

  #onLinkClick(e: ClickEvent<Document>) {
    e.preventDefault();

    const hash = this.#normalizeHash(String($(e.currentTarget).attr('href') || ''));
    if (!this.#isSectionHash(hash) || this.#isSketchHash(hash)) {
      return;
    }

    this.#setActiveLinks(hash);
    this.#pushHash(hash);
    this.#scrollToHash(hash, true);
  }

  #onHashChange() {
    const hash = this.#normalizeHash(window.location.hash);
    if (!this.#isSectionHash(hash) || this.#isSketchHash(hash)) {
      return;
    }

    this.#blockHistoryPush(900);
    this.#setActiveLinks(hash);
    this.#scrollToHash(hash, true);
  }

  #onScroll() {
    this.#handleHeaderPinnedState();

    if (!this.#shouldUpdateHashOnScroll() || this.#isProgrammaticScroll || this.#shouldMergeCompanyTabs()) {
      return;
    }

    this.#syncAboutCompanyHashOnMobile();
  }

  #handleHeaderPinnedState() {
    if (window.scrollY > this.#headerOffsetTop) {
      $('body').addClass(this.#headerPinnedSelector);
    } else {
      $('body').removeClass(this.#headerPinnedSelector);
    }
  }

  #syncActiveStateFromLocation() {
    const hash = this.#normalizeHash(window.location.hash || '#about');
    if (!this.#isSectionHash(hash) || this.#isSketchHash(hash)) {
      return;
    }

    this.#setActiveLinks(hash);
  }

  #syncAboutCompanyHashOnMobile() {
    const aboutTop = this.#getOffsetTop('#about');
    const companyTop = this.#getOffsetTop('#company');
    const projectsTop = this.#getOffsetTop('#projects');

    if (aboutTop === null || companyTop === null || projectsTop === null) {
      return;
    }

    const probe = window.scrollY + this.#getHeaderHeight();
    if (probe < aboutTop || probe >= projectsTop) {
      return;
    }

    const hash = probe >= companyTop ? '#company' : '#about';
    this.#setActiveLinks(hash);
    this.#pushHash(hash);
  }

  #scrollToHash(hash: string, animated: boolean) {
    const $target = $(hash);
    if (!$target.length) {
      return;
    }

    const top = $target.offset()?.top as number | undefined;
    if (typeof top !== 'number') {
      return;
    }

    const finalOffset = Math.max(0, Math.floor(top));
    if (!animated) {
      window.scrollTo(0, finalOffset);
      this.#scrollSpy?.refresh();
      return;
    }

    this.#isProgrammaticScroll = true;
    this.#blockHistoryPush(700);

    $('html, body')
      .stop(true)
      .animate({ scrollTop: finalOffset }, 500, () => {
        this.#isProgrammaticScroll = false;
        this.#blockHistoryPush(120);
        this.#scrollSpy?.refresh();
      });
  }

  #pushHash(hash: string) {
    if (!this.#isSectionHash(hash) || this.#isSketchHash(hash)) {
      return;
    }

    if (this.#isHistoryPushBlocked()) {
      return;
    }

    if (window.location.hash === hash) {
      return;
    }

    const now = Date.now();
    if (this.#lastPushedHash === hash && now - this.#lastPushedAt < HISTORY_DEDUPE_MS) {
      return;
    }

    history.pushState({}, '', hash);
    this.#lastPushedHash = hash;
    this.#lastPushedAt = now;
  }

  #blockHistoryPush(ms: number) {
    this.#historyBlockedUntil = Date.now() + ms;
  }

  #isHistoryPushBlocked(): boolean {
    return Date.now() < this.#historyBlockedUntil;
  }

  #setActiveLinks(hash: string) {
    if (!this.#isSectionHash(hash)) {
      return;
    }

    $(NAV_LINKS_SELECTOR).each((i, element) => {
      const $link = $(element);
      const active = ($link.attr('href') || '') === hash;
      $link.toggleClass('active', active);
    });
  }

  #extractHashFromActivateEvent(event: Event): string {
    const relatedTarget = (event as any).relatedTarget || (event as any)?.detail?.relatedTarget;
    if (!(relatedTarget instanceof HTMLAnchorElement)) {
      return '';
    }

    return relatedTarget.getAttribute('href') || relatedTarget.hash || '';
  }

  #normalizeHash(hash: string): string {
    if (this.#shouldMergeCompanyTabs() && hash === '#company') {
      return '#about';
    }

    return hash;
  }

  #shouldMergeCompanyTabs(): boolean {
    return window.innerWidth >= MERGE_COMPANY_BREAKPOINT;
  }

  #applyCompanyTabsMergeState() {
    const merge = this.#shouldMergeCompanyTabs();
    const navRoots = document.querySelectorAll('.app-nav, .app-mobile-nav');

    navRoots.forEach(navRoot => {
      const aboutLink = navRoot.querySelector('a[data-scroll][href="#about"]') as HTMLAnchorElement | null;
      const companyLink = navRoot.querySelector('a[data-scroll][href="#company"]') as HTMLAnchorElement | null;

      if (!aboutLink || !companyLink) {
        return;
      }

      if (!aboutLink.dataset.baseLabel) {
        aboutLink.dataset.baseLabel = (aboutLink.textContent || '').trim();
      }

      if (!aboutLink.dataset.companyLabel) {
        aboutLink.dataset.companyLabel = (companyLink.textContent || '').trim();
      }

      const companyItem = companyLink.closest('.app-nav__item, .app-mobile-nav__item') as HTMLElement | null;
      if (!companyItem) {
        return;
      }

      const aboutLabel = aboutLink.dataset.baseLabel || '';
      const companyLabel = aboutLink.dataset.companyLabel || '';

      if (merge) {
        aboutLink.textContent = companyLabel ? `${aboutLabel} / ${companyLabel}` : aboutLabel;
        companyItem.classList.add('is-nav-merged-hidden');
        companyItem.setAttribute('aria-hidden', 'true');
        return;
      }

      aboutLink.textContent = aboutLabel;
      companyItem.classList.remove('is-nav-merged-hidden');
      companyItem.removeAttribute('aria-hidden');
    });
  }

  #getHeaderHeight(): number {
    return ($(this.#headerSelector).outerHeight() as number) || 0;
  }

  #getOffsetTop(selector: string): number | null {
    const $el = $(selector);
    if (!$el.length) {
      return null;
    }

    const top = $el.offset()?.top as number | undefined;
    return typeof top === 'number' ? top : null;
  }

  #isSketchHash(hash: string): boolean {
    return hash.startsWith(SKETCH_HASH_PREFIX);
  }

  #isSectionHash(hash: string): boolean {
    return typeof hash === 'string' && hash.startsWith('#');
  }

  #shouldUpdateHashOnScroll(): boolean {
    return this.#syncHashOnScroll && window.innerWidth >= SCROLL_HASH_MIN_WIDTH;
  }
}

$(() => {
  new ScrollNavModule({
    linkSelector: 'a[data-scroll]',
    sectionSelector: 'section.app-section',
    headerSelector: 'header.app-header',
    headerPinned: true,
    headerOffsetTop: ($(window).height() as number) / 3,
    headerPinnedSelector: 'app-header-pinned',
    syncHashOnScroll: true,
  });
});
